// src/components/CustomCamera.js
import React, { useRef, useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

const CustomCamera = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
  const [error, setError] = useState('');

  useEffect(() => {
    const initCamera = async () => {
      // Stop any existing streams
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode, // Use the current facingMode state
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;

          // Handle the play() Promise
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                // Automatic playback started!
              })
              .catch((err) => {
                console.error('Error playing video:', err);
                setError('Unable to play the video stream.');
                // Stop the stream if playback fails
                mediaStream.getTracks().forEach((track) => track.stop());
              });
          }

          setStream(mediaStream);
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Unable to access the camera. Please check permissions.');
      }
    };

    initCamera();

    // Clean up the camera stream when component unmounts or facingMode changes
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]); // Dependency array includes facingMode

  const handleTakePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const dataUrl = canvas.toDataURL('image/jpeg');
      console.log('Photo captured:');
      onCapture(dataUrl);
    }
  };

  const switchCamera = () => {
    setFacingMode((prevMode) => (prevMode === 'user' ? 'environment' : 'user'));
  };

  if (error) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '400px',
          textAlign: 'center',
          color: 'red',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
        }}
      >
        <Typography variant="h6">{error}</Typography>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            marginTop: 2,
          }}
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '400px' }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '8px',
          backgroundColor: 'black',
        }}
        playsInline
        autoPlay
      ></video>
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
      
      {/* Capture Button */}
      <Button
        variant="contained"
        onClick={handleTakePhoto}
        sx={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          backgroundColor: '#1976d2',
          zIndex: 10,
        }}
      >
        Capture
      </Button>

      {/* Close Button */}
      <Button
        variant="outlined"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          borderColor: '#fff',
          color: '#fff',
        }}
      >
        <Close />
      </Button>

      {/* Switch Camera Button */}
      <Button
        variant="outlined"
        onClick={switchCamera}
        sx={{
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          borderColor: '#fff',
          color: '#fff',
          //marginLeft: 1, // Adds space between buttons
        }}
      >
        Switch Camera
      </Button>
    </div>
  );
};

export default CustomCamera;